import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { BarChart3, FileDown, MinusCircle, PlusCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PKHakedis() {
  const [firmalar, setFirmalar] = useState([]);
  const [hakedisData, setHakedisData] = useState(null);
  const [selectedFirma, setSelectedFirma] = useState('');
  const [donemAy, setDonemAy] = useState(new Date().getMonth() + 1);
  const [donemYil, setDonemYil] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);

  useEffect(() => {
    axios.get(`${API}/pk/firmalar`, { withCredentials: true })
      .then(r => setFirmalar(r.data))
      .finally(() => setLoading(false));
  }, []);

  const hesapla = async () => {
    if (!selectedFirma) return;
    setCalculating(true);
    try {
      const res = await axios.post(`${API}/pk/hakedis/hesapla`, {
        firma_id: selectedFirma, donem_ay: donemAy, donem_yil: donemYil
      }, { withCredentials: true });
      setHakedisData(res.data);
    } catch (err) {
      alert('Hata: ' + (err.response?.data?.detail || err.message));
    } finally { setCalculating(false); }
  };

  const formatMoney = (val) => {
    const n = parseFloat(val || 0);
    return n.toLocaleString('tr-TR', { minimumFractionDigits: 2 }) + ' TL';
  };

  const getFirmaAdi = (id) => firmalar.find(f => f.id === id)?.unvan || id;

  const AY_ISIMLERI = ['', 'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];

  if (loading) return <div className="text-gray-400 text-center py-8">Yükleniyor...</div>;

  return (
    <div className="space-y-6" data-testid="pk-hakedis">
      <h2 className="text-xl font-bold text-white flex items-center gap-2"><BarChart3 className="w-5 h-5 text-blue-400" /> Hakediş Raporu</h2>

      {/* Seçim Paneli */}
      <Card className="bg-gray-900/80 border-gray-700/50">
        <CardContent className="p-4">
          <div className="flex items-end gap-4 flex-wrap">
            <div className="space-y-1 flex-1 min-w-[200px]">
              <label className="text-xs text-gray-400">Firma</label>
              <Select value={selectedFirma} onValueChange={setSelectedFirma}>
                <SelectTrigger className="bg-gray-800 border-gray-700 text-white"><SelectValue placeholder="Firma seçin" /></SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-700 text-white">{firmalar.map(f => <SelectItem key={f.id} value={f.id}>{f.unvan}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1 w-[120px]">
              <label className="text-xs text-gray-400">Ay</label>
              <Select value={String(donemAy)} onValueChange={v => setDonemAy(parseInt(v))}>
                <SelectTrigger className="bg-gray-800 border-gray-700 text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-700 text-white">
                  {AY_ISIMLERI.slice(1).map((ay, i) => <SelectItem key={i+1} value={String(i+1)}>{ay}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1 w-[100px]">
              <label className="text-xs text-gray-400">Yıl</label>
              <Select value={String(donemYil)} onValueChange={v => setDonemYil(parseInt(v))}>
                <SelectTrigger className="bg-gray-800 border-gray-700 text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-700 text-white">
                  {[2024, 2025, 2026, 2027, 2028].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={hesapla} disabled={calculating || !selectedFirma} className="bg-blue-600 hover:bg-blue-700" data-testid="hesapla-btn">
              {calculating ? 'Hesaplanıyor...' : 'Hesapla'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Sonuç */}
      {hakedisData && (
        <Card className="bg-gray-900/80 border-gray-700/50">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-bold text-white">{getFirmaAdi(hakedisData.firma_id)}</h3>
                <p className="text-sm text-gray-400">{AY_ISIMLERI[hakedisData.donem_ay]} {hakedisData.donem_yil} Dönemi</p>
              </div>
              <Badge className={`text-xs ${hakedisData.onay_durumu === 'onaylandi' ? 'bg-green-700' : hakedisData.onay_durumu === 'reddedildi' ? 'bg-red-700' : 'bg-yellow-700 text-black'}`}>
                {hakedisData.onay_durumu === 'taslak' ? 'Taslak' : hakedisData.onay_durumu === 'onaylandi' ? 'Onaylandı' : 'Reddedildi'}
              </Badge>
            </div>

            <div className="border border-gray-700 rounded-lg overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-800/50 text-xs text-gray-400">
                    <th className="text-left py-3 px-4">Kalem</th>
                    <th className="text-right py-3 px-4">Tutar</th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  <tr className="border-t border-gray-800">
                    <td className="py-3 px-4 text-white flex items-center gap-2"><PlusCircle className="w-4 h-4 text-green-400" /> Brüt Hakediş</td>
                    <td className="py-3 px-4 text-right text-green-400 font-medium">{formatMoney(hakedisData.brut_hakedis)}</td>
                  </tr>
                  <tr className="border-t border-gray-800">
                    <td className="py-3 px-4 text-white flex items-center gap-2"><MinusCircle className="w-4 h-4 text-red-400" /> SLA İhlal Cezaları ({hakedisData.ceza_detaylari?.length || 0})</td>
                    <td className="py-3 px-4 text-right text-red-400 font-medium">-{formatMoney(hakedisData.toplam_ceza_kesintisi)}</td>
                  </tr>
                  <tr className="border-t border-gray-800">
                    <td className="py-3 px-4 text-white flex items-center gap-2"><MinusCircle className="w-4 h-4 text-orange-400" /> Kullanım Dışı Cihaz ({hakedisData.kullanim_disi_count || 0} cihaz)</td>
                    <td className="py-3 px-4 text-right text-orange-400 font-medium">-{formatMoney(hakedisData.kullanim_disi_cihaz_kesintisi)}</td>
                  </tr>
                  <tr className="border-t-2 border-blue-600 bg-blue-900/20">
                    <td className="py-4 px-4 text-white font-bold text-base">Net Hakediş</td>
                    <td className="py-4 px-4 text-right text-blue-300 font-bold text-lg">{formatMoney(hakedisData.net_hakedis)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Ceza Detayları */}
            {hakedisData.ceza_detaylari?.length > 0 && (
              <div className="mt-4 p-3 bg-red-900/10 border border-red-800/30 rounded-lg">
                <p className="text-xs font-semibold text-red-400 mb-2">Ceza Detayları:</p>
                {hakedisData.ceza_detaylari.map((c, i) => (
                  <div key={i} className="flex justify-between text-xs text-gray-400 py-1 border-b border-red-900/20 last:border-0">
                    <span>Arıza #{i + 1} — {c.gecikme_saat?.toFixed(1)}h gecikme</span>
                    <span className="text-red-400">{formatMoney(c.ceza_tutar)}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
